<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'

/**
 * PSlideOver — panneau qui glisse depuis la droite, plein hauteur.
 *
 * Pattern Revelio §5.7. Usage typique : drill-down d'une row de table
 * (cliquer une row → ouvrir le détail dans un panel droit sans changer
 * de route).
 *
 * Sur mobile : prend toute la largeur ; sur desktop, max-width 480px.
 *
 * Footer optionnel (slot footer) — utile pour actions sticky (Save / Delete).
 */
interface Props {
    open: boolean
    title?: string
    /** Width sur desktop. md = 480, lg = 640. */
    width?: 'md' | 'lg'
    persistent?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    width: 'md',
    persistent: false,
})

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'close'): void
}>()

function close() {
    emit('update:open', false)
    emit('close')
}

function onBackdropClick() {
    if (!props.persistent) close()
}

function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && props.open && !props.persistent) {
        close()
    }
}

watch(
    () => props.open,
    (isOpen) => {
        if (typeof document === 'undefined') return
        document.body.style.overflow = isOpen ? 'hidden' : ''
    },
)

onMounted(() => {
    document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown)
    if (typeof document !== 'undefined') {
        document.body.style.overflow = ''
    }
})
</script>

<template>
    <Teleport to="body">
        <Transition name="fade">
            <div
                v-if="open"
                class="p-slideover-backdrop"
                role="presentation"
                @click.self="onBackdropClick"
            ></div>
        </Transition>
        <Transition name="slide-right">
            <aside
                v-if="open"
                :class="['p-slideover', `p-slideover--${width}`]"
                role="dialog"
                aria-modal="true"
                :aria-labelledby="title ? 'p-slideover-title' : undefined"
            >
                <header class="p-slideover__header">
                    <p v-if="title" id="p-slideover-title" class="p-slideover__title">
                        {{ title }}
                    </p>
                    <slot v-else name="header" />
                    <button
                        v-if="!persistent"
                        type="button"
                        class="p-slideover__close"
                        :aria-label="$t('common.close')"
                        @click="close"
                    >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                            <path d="M3 3l10 10M13 3L3 13" />
                        </svg>
                    </button>
                </header>
                <div class="p-slideover__body">
                    <slot />
                </div>
                <footer v-if="$slots.footer" class="p-slideover__footer">
                    <slot name="footer" />
                </footer>
            </aside>
        </Transition>
    </Teleport>
</template>

<style scoped>
.p-slideover-backdrop {
    position: fixed;
    inset: 0;
    background: var(--p-color-overlay);
    z-index: 999;
}

.p-slideover {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 100%;
    background: var(--p-color-surface);
    border-left: 1px solid var(--p-color-line);
    box-shadow: var(--p-shadow-modal);
    display: flex;
    flex-direction: column;
    z-index: 1000;
}

@media (min-width: 640px) {
    .p-slideover--md {
        max-width: 480px;
    }
    .p-slideover--lg {
        max-width: 640px;
    }
}

.p-slideover__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    height: 56px;
    border-bottom: 1px solid var(--p-color-line);
    flex-shrink: 0;
}

.p-slideover__title {
    margin: 0;
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    font-weight: var(--p-font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
}

.p-slideover__close {
    background: transparent;
    border: none;
    color: var(--p-color-ink-4);
    cursor: pointer;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--p-radius-sm);
    transition:
        background var(--p-motion-fast) var(--p-ease-out),
        color var(--p-motion-fast) var(--p-ease-out);
}
.p-slideover__close:hover {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink);
}

.p-slideover__body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
}

.p-slideover__footer {
    border-top: 1px solid var(--p-color-line);
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--p-space-3);
    flex-shrink: 0;
}
</style>
